import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Phone, MapPin, Clock, DollarSign, CheckCircle, MessageSquare, Trash2, MessageCircle } from 'lucide-react';
import type { Booking, Therapist, TranslateFn } from '../../types';

interface BookingsTabProps {
  bookings: Booking[];
  t: TranslateFn;
  getStatusBadge: (status: string) => React.ReactNode;
  formatDate: (date: string) => string;
  setAssignDialog: (data: { open: boolean; booking: Booking }) => void;
  handleUpdateStatus: (id: string, status: string) => void;
  handleDeleteBooking: (id: string) => void;
  onOpenChat: (booking: Booking) => void;
}

export const BookingsTab = ({ bookings, t, getStatusBadge, formatDate, setAssignDialog, handleUpdateStatus, handleDeleteBooking, onOpenChat }: BookingsTabProps) => (
  <div className="space-y-4">
    {bookings.length === 0 ? (
      <Card>
        <CardContent className="py-8 text-center text-gray-500">
          {t('admin.noBookings')}
        </CardContent>
      </Card>
    ) : (
      bookings.map((booking) => (
        <Card key={booking.id} data-testid={`booking-card-${booking.id}`}>
          <CardContent className="pt-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-lg">{booking.name}</h3>
                  {getStatusBadge(booking.status)}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {booking.contact_number}
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {booking.hotel_name}, {t('admin.room')} {booking.room_number}
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {booking.service_type} ({booking.duration_minutes}{t('common.min')})
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    ฿{booking.price_thb}
                  </div>
                </div>
                
                {(booking.line_id || booking.whatsapp_number) && (
                  <div className="flex items-center gap-4 text-sm">
                    {booking.line_id && (
                      <span className="text-green-600">
                        <MessageSquare className="w-4 h-4 inline mr-1" />
                        LINE: {booking.line_id.startsWith('@') ? booking.line_id : `@${booking.line_id}`}
                      </span>
                    )}
                    {booking.whatsapp_number && (
                      <span className="text-green-600">
                        WhatsApp: {booking.whatsapp_number}
                      </span>
                    )}
                  </div>
                )}
                
                {booking.preferred_therapist && booking.preferred_therapist !== 'none' && !booking.assigned_therapist_name && (
                  <div className="text-sm text-pink-600">
                    <span className="font-medium">{t('admin.requestedTherapist') || 'Requested'}:</span> {booking.preferred_therapist}
                  </div>
                )}
                
                {booking.assigned_therapist_name && (
                  <div className="text-sm">
                    <span className="font-medium">{t('admin.assignedTo')}:</span> {booking.assigned_therapist_name}
                    {booking.therapist_accepted && <CheckCircle className="w-4 h-4 inline ml-2 text-green-600" />}
                  </div>
                )}

                {booking.booking_datetime && (
                  <div className="text-sm" data-testid={`booking-time-${booking.id}`}>
                    <span className="font-bold text-red-600">Booking Time: {(() => {
                      try {
                        const d = new Date(booking.booking_datetime);
                        return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
                          + ' at ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                      } catch { return booking.booking_datetime; }
                    })()}</span>
                  </div>
                )}

                {booking.special_requests && (
                  <div className="text-sm bg-yellow-50 border border-yellow-200 rounded px-2 py-1 mt-1" data-testid={`special-requests-${booking.id}`}>
                    <span className="font-medium text-yellow-800">Special Requests:</span>{' '}
                    <span className="text-yellow-700">{booking.special_requests}</span>
                  </div>
                )}
                
                {booking.distance_km != null && (
                  <div className={`text-sm ${booking.distance_warning ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                    {booking.distance_km}km from Sukhumvit 20
                    {booking.taxi_surcharge > 0 && (
                      <span className="ml-1 font-semibold text-amber-600">
                        — Taxi: {booking.taxi_surcharge} THB
                      </span>
                    )}
                    {booking.taxi_surcharge === -1 && (
                      <span className="ml-1 font-semibold text-red-600">
                        — Taxi: TBC
                      </span>
                    )}
                    {!booking.distance_warning && ' (included)'}
                  </div>
                )}
                
                <div className="text-xs text-gray-400">
                  {t('admin.created')}: {formatDate(booking.created_at)}
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {booking.status !== 'completed' && booking.status !== 'cancelled' && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="gap-1 border-pink-300 text-pink-600 hover:bg-pink-50"
                    onClick={() => onOpenChat(booking)}
                    data-testid={`admin-chat-btn-${booking.id}`}
                  >
                    <MessageCircle className="w-4 h-4" />
                    Chat
                  </Button>
                )}
                {booking.status === 'pending' && (
                  <Button 
                    size="sm" 
                    onClick={() => setAssignDialog({ open: true, booking })}
                    data-testid={`assign-btn-${booking.id}`}
                  >
                    {t('admin.assignTherapist')}
                  </Button>
                )}
                {booking.status === 'assigned' && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setAssignDialog({ open: true, booking })}
                  >
                    {t('admin.reassign')}
                  </Button>
                )}
                {booking.status !== 'completed' && booking.status !== 'cancelled' && (
                  <>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleUpdateStatus(booking.id, 'completed')}
                    >
                      {t('admin.markComplete')}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => handleUpdateStatus(booking.id, 'cancelled')}
                    >
                      {t('common.cancel')}
                    </Button>
                  </>
                )}
                <Button 
                  size="sm" 
                  variant="ghost"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDeleteBooking(booking.id)}
                  data-testid={`delete-booking-${booking.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))
    )}
  </div>
);
